import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Loader2, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Buy, sell and trade rifles, shotguns, handguns, NFA items and ammo",
  "List firearms with optional FFL transfer requirements",
  "Direct-message firearm buyers and sellers",
  "Supports Second Amendment education & advocacy",
];

export default function Subscribe() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const start = async (plan) => {
    if (!user) return navigate("/login");
    setLoading(plan);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setLoading(null);
    }
  };

  const isActive = user?.subscription_status === "active" || user?.subscription_status === "trialing";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 animate-fade-in" data-testid="subscribe-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-secondary mb-3">
          <Shield className="w-3.5 h-3.5" /> Firearms Pass · 2A supporters
        </div>
        <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">
          The <span className="text-secondary">Firearms Pass</span>
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          To keep the firearms marketplace serious, verified, and lawful, buying and selling any firearm, NFA item or
          ammunition on Florida Sportsman requires an active Firearms Pass. Everything else on the site stays free.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-3xl mx-auto">
        <PlanCard
          title="Monthly"
          price="$12.97"
          suffix="/ month"
          tag={null}
          onClick={() => start("monthly")}
          loading={loading === "monthly"}
          disabled={isActive}
          testId="btn-plan-monthly"
        />
        <PlanCard
          title="Annual"
          price="$100"
          suffix="/ year"
          tag="Save 36%"
          highlight
          onClick={() => start("annual")}
          loading={loading === "annual"}
          disabled={isActive}
          testId="btn-plan-annual"
        />
      </div>

      <Card className="mt-12 bg-card border-border max-w-3xl mx-auto">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg">What you get</h3>
          <ul className="mt-4 space-y-3">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {isActive && (
        <div className="max-w-3xl mx-auto mt-8 bg-primary/5 border border-primary/30 rounded-md p-5 text-sm flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-primary">Your Firearms Pass is active.</div>
            <div className="text-muted-foreground text-xs mt-1">
              You can browse and post in all firearms categories.
            </div>
          </div>
          <Button onClick={() => navigate(`/profile/${user.user_id}`)} variant="outline" data-testid="btn-manage-sub">
            Manage <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-10 max-w-xl mx-auto leading-relaxed">
        Cancel anytime from your profile. Recurring charges are processed by Stripe.
        Florida Sportsman is a proud supporter of the Second Amendment and the NRA.
      </p>
    </div>
  );
}

const PlanCard = ({ title, price, suffix, tag, highlight, onClick, loading, disabled, testId }) => (
  <div
    className={`relative rounded-md border p-6 transition-all ${
      highlight ? "border-secondary bg-card shadow-[0_16px_40px_rgba(192,92,66,0.15)]" : "border-border bg-card"
    }`}
  >
    {tag && (
      <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest bg-secondary text-secondary-foreground px-2 py-1 rounded">
        {tag}
      </div>
    )}
    <div className="font-display font-bold text-lg">{title}</div>
    <div className="mt-3 flex items-baseline gap-1">
      <span className="font-display font-extrabold text-5xl">{price}</span>
      <span className="text-muted-foreground text-sm">{suffix}</span>
    </div>
    <Button
      onClick={onClick}
      disabled={loading || disabled}
      size="lg"
      className={`mt-6 w-full h-12 ${highlight ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"}`}
      data-testid={testId}
    >
      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {disabled ? "Already subscribed" : "Get the Pass"}
    </Button>
  </div>
);
